'use client';

import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, ScatterChart, Scatter } from 'recharts';

interface Order {
  numeroOrdem: string;
  value: number;
}

interface Quadrant {
  orders: Order[];
  count: number;
  total_value: number;
  total_qty: number;
  percentage: number;
  avg_value: number;
}

interface PriceQuadrantsData {
  quadrants: {
    low_price_low_qty: Quadrant;
    low_price_high_qty: Quadrant;
    high_price_low_qty: Quadrant;
    high_price_high_qty: Quadrant;
  };
  summary: {
    total_orders: number;
    total_value: number;
    price_median: number;
  };
}

interface PriceQuadrantsProps {
  data: PriceQuadrantsData;
}

const quadrantConfig = {
  low_price_low_qty: {
    label: 'Baixo Preço × Baixa Frequência',
    color: '#8b5cf6',
    shortLabel: 'BP×BF',
  },
  low_price_high_qty: {
    label: 'Baixo Preço × Alta Frequência',
    color: '#06b6d4',
    shortLabel: 'BP×AF',
  },
  high_price_low_qty: {
    label: 'Alto Preço × Baixa Frequência',
    color: '#f97316',
    shortLabel: 'AP×BF',
  },
  high_price_high_qty: {
    label: 'Alto Preço × Alta Frequência',
    color: '#10b981',
    shortLabel: 'AP×AF',
  },
};

export function PriceQuadrants({ data }: PriceQuadrantsProps) {
  const { quadrants, summary } = data;

  // Prepare data for bar chart
  const chartData = Object.entries(quadrants).map(([key, quadrant]) => ({
    name: quadrantConfig[key as keyof typeof quadrantConfig].shortLabel,
    count: quadrant.count,
    total_value: quadrant.total_value,
    avg_value: quadrant.avg_value,
    percentage: quadrant.percentage,
  }));

  return (
    <div className="rounded-lg border p-6 mb-8" style={{
      backgroundColor: 'var(--card-bg)',
      borderColor: 'var(--card-border-bright)',
      backdropFilter: 'blur(10px)',
      marginTop: '1em',
      padding: '1em',
    }}>
      <h2 className="text-xl font-bold mb-6 text-center" style={{
        marginBottom: '1em'
      }}>Análise de Quadrantes de Preço</h2>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-6 mb-8" style={{ marginBottom: '1em' }}>
        <div className="p-6 rounded-lg border text-center" style={{ 
          backgroundColor: 'rgba(59, 130, 246, 0.1)', 
          borderColor: 'rgba(59, 130, 246, 0.3)',
          borderWidth: '2px'
        }}>
          <p className="text-sm text-slate-400">Total de Pedidos</p>
          <p className="text-2xl font-bold text-blue-400">{summary.total_orders}</p>
        </div>
        <div className="p-4 rounded-lg border text-center" style={{ 
          backgroundColor: 'rgba(16, 185, 129, 0.1)', 
          borderColor: 'rgba(16, 185, 129, 0.3)',
          borderWidth: '2px',
        }}>
          <p className="text-sm text-slate-400">Valor Total</p>
          <p className="text-2xl font-bold text-green-400">R$ {summary.total_value ? summary.total_value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0,00'}</p>
        </div>
        <div className="p-4 rounded-lg border text-center" style={{ 
          backgroundColor: 'rgba(168, 85, 247, 0.1)', 
          borderColor: 'rgba(168, 85, 247, 0.3)',
          borderWidth: '2px'
        }}>
          <p className="text-sm text-slate-400">Preço Mediano</p>
          <p className="text-2xl font-bold text-purple-400">R$ {summary.price_median ? summary.price_median.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0,00'}</p>
        </div>
      </div>

      {/* Quadrants Overview */}
      <div className="grid grid-cols-2 gap-6 mb-8">
        {Object.entries(quadrants).map(([key, quadrant]) => {
          const config = quadrantConfig[key as keyof typeof quadrantConfig];
          return (
            <div key={key} className="p-6 rounded-lg border" style={{ 
              backgroundColor: config.color + '15', 
              borderColor: config.color + '40',
              borderWidth: '2px',
              padding: '1em',
            }}>
              <h3 className="font-semibold mb-3" style={{ color: config.color }}>
                {config.label}
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Pedidos:</span>
                  <span className="font-semibold">{quadrant.count}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Valor Total:</span>
                  <span className="font-semibold text-green-400">R$ {quadrant.total_value ? quadrant.total_value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0,00'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">% do Total:</span>
                  <span className="font-semibold text-blue-400">{quadrant.percentage ? quadrant.percentage.toFixed(1) : '0.0'}%</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Valor Médio:</span>
                  <span className="font-semibold text-yellow-400">R$ {quadrant.avg_value ? quadrant.avg_value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0,00'}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Bar Chart - Count and Value Distribution */}
      <div className="mb-8 rounded-lg border p-4" style={{ borderColor: 'var(--card-border)', padding: '1em', marginTop: '1em'}}>
        <h3 className="text-sm font-semibold text-slate-400 mb-4">Distribuição de Quantidade e Valor</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.2)" />
            <XAxis dataKey="name" stroke="rgba(148, 163, 184, 0.5)" />
            <YAxis stroke="rgba(148, 163, 184, 0.5)" />
            <Tooltip
              contentStyle={{
                backgroundColor: 'rgba(30, 41, 59, 0.9)',
                border: '1px solid rgba(148, 163, 184, 0.3)',
                borderRadius: '8px',
              }}
            />
            <Legend />
            <Bar dataKey="count" fill="#8b5cf6" name="Quantidade de Pedidos" />
            <Bar dataKey="percentage" fill="#06b6d4" name="% do Valor Total" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Detailed Tables */}
      <div className="grid grid-cols-2 gap-6" style={{ marginBottom: '1em', marginTop: '1em' }}>
        {Object.entries(quadrants).map(([key, quadrant]) => {
          const config = quadrantConfig[key as keyof typeof quadrantConfig];
          return (
            <div key={key} className="overflow-x-auto rounded-lg border" style={{ borderColor: 'var(--card-border)', padding: '1em' }}>
              <h3 className="text-sm font-semibold text-slate-400 px-4 pt-4" style={{ color: config.color }}>
                {config.shortLabel} - Top 10 Pedidos
              </h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b" style={{ borderColor: 'var(--card-border)' }}>
                    <th className="text-left py-3 px-4 text-slate-400 font-semibold">Pedido</th>
                    <th className="text-right py-3 px-4 text-slate-400 font-semibold">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {quadrant.orders.slice(0, 10).map((order, idx) => (
                    <tr key={order.numeroOrdem} className={idx !== 9 ? "border-b" : ""} style={{ borderColor: 'var(--card-border)' }}>
                      <td className="py-3 px-4 font-medium text-slate-200">{order.numeroOrdem}</td>
                      <td className="text-right py-3 px-4" style={{ color: config.color }}>R$ {order.value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    </tr>
                  ))}
                  {quadrant.orders.length === 0 && (
                    <tr>
                      <td colSpan={2} className="py-4 px-4 text-center text-slate-500">Nenhum pedido neste quadrante</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>
    </div>
  );
}
